/*
 * Попап плитки «Алерты» на главной: ресурсы с активными алертами Prometheus и их число
 * (кластер — все алерты своего Prometheus, остальные — своя часть, lib/alerts). Клик по
 * строке открывает вкладку «Метрики» ресурса.
 */
import { KeyboardEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useNavigate } from 'react-router-dom';
import { Entity } from '@backstage/catalog-model';
import { StatusError, StatusPending } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { ResourceAlerts, alertSummary } from '../../lib/alerts';
import {
  AIRFLOW_TYPE,
  BROKER_TYPE,
  DATABASE_TYPE,
  DEFECTDOJO_TYPE,
  KUBERNETES_CLUSTER_TYPE,
  ML_MODEL_TYPE,
  SERVICE_TYPE,
} from '../../lib/viva';

const useStyles = makeStyles(theme => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  row: { cursor: 'pointer' },
  name: { fontWeight: 700, whiteSpace: 'nowrap' },
  kind: { color: theme.palette.text.secondary, whiteSpace: 'nowrap' },
  top: {
    color: theme.palette.text.secondary,
    maxWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  count: { whiteSpace: 'nowrap' },
  muted: { color: theme.palette.text.secondary },
}));

const KIND_LABELS: Record<string, string> = {
  [KUBERNETES_CLUSTER_TYPE]: 'кластер',
  [DATABASE_TYPE]: 'PostgreSQL',
  [BROKER_TYPE]: 'Kafka',
  [SERVICE_TYPE]: 'сервис',
  [ML_MODEL_TYPE]: 'модель',
  [AIRFLOW_TYPE]: 'Airflow',
  [DEFECTDOJO_TYPE]: 'DefectDojo',
};

const kindLabel = (entity: Entity): string =>
  KIND_LABELS[String(entity.spec?.type)] ?? String(entity.spec?.type ?? '');

export const AlertsDialog = ({
  open,
  rows,
  unavailable,
  onClose,
}: {
  open: boolean;
  rows: ResourceAlerts[];
  /** Кластеры, чей Prometheus не ответил. */
  unavailable: string[];
  onClose: () => void;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const openMetrics = (entity: Entity) => {
    onClose();
    navigate(
      `${entityRoute({
        namespace: entity.metadata.namespace ?? 'default',
        kind: entity.kind.toLowerCase(),
        name: entity.metadata.name,
      })}/metrics`,
    );
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6">Алерты Prometheus</Typography>
        <IconButton aria-label="Закрыть" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ресурс</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Firing</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Самый важный</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ entity, alerts, count }) => {
                const go = () => openMetrics(entity);
                const onKey = (e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    go();
                  }
                };
                return (
                  <TableRow
                    key={`${entity.kind}/${entity.metadata.name}`}
                    hover
                    className={classes.row}
                    onClick={go}
                    onKeyDown={onKey}
                    role="button"
                    tabIndex={0}
                  >
                    <TableCell className={classes.name}>
                      {entity.metadata.title ?? entity.metadata.name}
                    </TableCell>
                    <TableCell className={classes.kind}>
                      {kindLabel(entity)}
                    </TableCell>
                    <TableCell className={classes.count}>
                      {count.firing > 0 ? (
                        <StatusError>{count.firing}</StatusError>
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>
                    <TableCell className={classes.count}>
                      {count.pending > 0 ? (
                        <StatusPending>{count.pending}</StatusPending>
                      ) : (
                        <span className={classes.muted}>—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className={classes.top}
                      title={`${alerts[0].name}: ${alertSummary(alerts[0])}`}
                    >
                      {alerts[0].name} — {alertSummary(alerts[0])}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <Typography
          variant="body2"
          className={classes.muted}
          style={{ marginTop: 12 }}
        >
          {rows.length === 0
            ? 'Активных алертов нет.'
            : 'Кластер — все алерты своего Prometheus, остальные ресурсы — их доля по меткам. Клик по строке открывает вкладку «Метрики».'}
          {unavailable.length > 0 &&
            ` Prometheus недоступен: ${unavailable.join(', ')}.`}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};
